import { useState, useCallback } from 'react';

export interface SectionState {
  [sectionKey: string]: boolean;
}

export interface UseCollapsibleSectionsReturn {
  /**
   * Поточні стани всіх секцій
   */
  sections: SectionState;

  /**
   * Перемикач стану конкретної секції
   */
  toggleSection: (sectionKey: string) => void;

  /**
   * Встановлення стану конкретної секції
   */
  setSectionState: (sectionKey: string, collapsed: boolean) => void;

  /**
   * Отримання стану конкретної секції
   */
  getSectionState: (sectionKey: string) => boolean;

  /**
   * Згорнути всі секції
   */
  collapseAll: () => void;

  /**
   * Розгорнути всі секції
   */
  expandAll: () => void;
}

/**
 * Хук для керування станами collapsible секцій з збереженням в localStorage
 *
 * @param pageKey - унікальний ключ сторінки для localStorage
 * @param defaultStates - початкові стани секцій
 * @returns об'єкт з методами керування станами секцій
 */
export const useCollapsibleSections = (
  pageKey: string,
  defaultStates: SectionState = {}
): UseCollapsibleSectionsReturn => {
  // Ініціалізуємо стани з localStorage або дефолтними значеннями
  const [sections, setSections] = useState<SectionState>(() => {
    try {
      const savedStates = localStorage.getItem(`collapsible-sections-${pageKey}`);
      if (savedStates) {
        const parsed = JSON.parse(savedStates);
        // Мержимо збережені стани з дефолтними (на випадок нових секцій)
        return { ...defaultStates, ...parsed };
      }
    } catch (error) {
      console.warn('Failed to load collapsible sections state from localStorage:', error);
    }
    return defaultStates;
  });

  // Зберігаємо стани в localStorage
  const saveToStorage = useCallback((newStates: SectionState) => {
    try {
      localStorage.setItem(`collapsible-sections-${pageKey}`, JSON.stringify(newStates));
    } catch (error) {
      console.warn('Failed to save collapsible sections state to localStorage:', error);
    }
  }, [pageKey]);

  // Перемикач стану секції
  const toggleSection = useCallback((sectionKey: string) => {
    setSections(prevStates => {
      const currentState = prevStates[sectionKey] || false;
      const newStates = {
        ...prevStates,
        [sectionKey]: !currentState
      };
      saveToStorage(newStates);
      return newStates;
    });
  }, [saveToStorage]);

  // Встановлення конкретного стану секції
  const setSectionState = useCallback((sectionKey: string, collapsed: boolean) => {
    setSections(prevStates => {
      const newStates = {
        ...prevStates,
        [sectionKey]: collapsed
      };
      saveToStorage(newStates);
      return newStates;
    });
  }, [saveToStorage]);

  // Отримання стану секції
  const getSectionState = useCallback((sectionKey: string): boolean => {
    return sections[sectionKey] || false;
  }, [sections]);

  // Згорнути всі секції
  const collapseAll = useCallback(() => {
    setSections(prevStates => {
      const newStates = Object.keys(prevStates).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {} as SectionState);
      saveToStorage(newStates);
      return newStates;
    });
  }, [saveToStorage]);

  // Розгорнути всі секції
  const expandAll = useCallback(() => {
    setSections(prevStates => {
      const newStates = Object.keys(prevStates).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as SectionState);
      saveToStorage(newStates);
      return newStates;
    });
  }, [saveToStorage]);

  return {
    sections,
    toggleSection,
    setSectionState,
    getSectionState,
    collapseAll,
    expandAll
  };
};